/**
 * Tooltip — a Radix Tooltip styled as a small espresso bubble. Wrap the app (or
 * a subtree) once in <TooltipProvider>, then use <Tooltip content="…">. The
 * trigger child must forward a ref / props (asChild).
 */
import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixTooltip from "@radix-ui/react-tooltip";

export interface TooltipProviderProps
  extends ComponentPropsWithoutRef<typeof RadixTooltip.Provider> {}

/** Provider — mount once near the app root. Sensible cream defaults. */
export function TooltipProvider({
  delayDuration = 200,
  skipDelayDuration = 300,
  children,
  ...rest
}: TooltipProviderProps) {
  return (
    <RadixTooltip.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...rest}
    >
      {children}
    </RadixTooltip.Provider>
  );
}

export interface TooltipProps {
  /** Tooltip text / node. */
  readonly content: ReactNode;
  /** The trigger element (rendered asChild). */
  readonly children: ReactNode;
  /** Preferred side. @default "top" */
  readonly side?: ComponentPropsWithoutRef<typeof RadixTooltip.Content>["side"];
  /** Offset from the trigger. @default 6 */
  readonly sideOffset?: number;
  /** Open delay override (ms). */
  readonly delayDuration?: number;
}

export function Tooltip({
  content,
  children,
  side = "top",
  sideOffset = 6,
  delayDuration,
}: TooltipProps) {
  return (
    <RadixTooltip.Root delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content className="lc-tooltip" side={side} sideOffset={sideOffset}>
          {content}
          <RadixTooltip.Arrow className="lc-tooltip__arrow" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
